/**
 * Job application form (careers page), progressive enhancement. Without JavaScript the form
 * still POSTs to /api/apply and the server re-renders errors or redirects to /thank-you.
 * With it: phone formatting, validation on blur, an error summary that takes focus, a fetch()
 * submit with spinner and check mark, then an inline thank-you. Messages live in the markup
 * (data-error-* attributes); lib/validate.js remains the source of truth.
 */
import { scrollToTarget } from './lenis.js';
import { formatPhone, showSuccess } from './form.js';

const MIN_FILL_MS = 3200;
const LABELS = { name: 'Full name', email: 'Email', phone: 'Phone', city: 'City', position: 'Role', availability: 'Availability', experience: 'Experience', cpr: 'CPR certification', message: 'About you', consent: 'Confirmation' };
const digits = (v) => String(v || '').replace(/\D/g, '');

function validate(form, name) {
  const wrap = form.querySelector(`[data-field="${name}"]`);
  if (wrap && wrap.dataset.groupRequired) {
    return form.querySelectorAll(`[name="${name}"]:checked`).length ? '' : wrap.dataset.groupRequired;
  }
  const el = form.elements[name];
  if (!el) return '';
  if (el.type === 'checkbox') return el.checked ? '' : el.dataset.errorRequired;
  const v = el.value.trim();
  if (!v) return el.dataset.errorRequired || 'This field is required.';
  if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) return el.dataset.errorType;
  if (name === 'phone') {
    const d = digits(v);
    if (!(d.length === 10 || (d.length === 11 && d.startsWith('1')))) return 'Please enter a 10 digit US phone number, like (313) 555-0123.';
  }
  if (name === 'message' && v.length < 20) return el.dataset.errorShort;
  return '';
}

function setError(form, name, message) {
  const wrap = form.querySelector(`[data-field="${name}"]`);
  if (!wrap) return;
  wrap.classList.toggle('is-invalid', Boolean(message));
  const text = wrap.querySelector('[data-error-text]');
  if (text) text.textContent = message || '';
  const target = wrap.dataset.groupRequired ? wrap : form.elements[name];
  if (!target || !target.setAttribute) return;
  if (message) target.setAttribute('aria-invalid', 'true');
  else target.removeAttribute('aria-invalid');
}

function firstControl(form, name) {
  return form.querySelector(`[name="${name}"]`);
}

function showSummary(form, errors, heading) {
  const summary = form.querySelector('[data-error-summary]');
  const list = summary.querySelector('[data-error-list]');
  summary.querySelector('[data-error-summary-title]').textContent = heading || 'Please check the highlighted fields.';
  list.innerHTML = '';
  Object.entries(errors || {}).forEach(([name, msg]) => {
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#apply-${name}`;
    a.textContent = `${LABELS[name] || name}: ${msg}`;
    a.addEventListener('click', (e) => {
      e.preventDefault();
      const control = firstControl(form, name);
      scrollToTarget(control.closest('.field') || control);
      setTimeout(() => control.focus(), 50);
    });
    li.appendChild(a);
    list.appendChild(li);
  });
  summary.hidden = false;
  summary.focus();
}

export function initApplyForms() {
  const loadedAt = Date.now();
  document.querySelectorAll('[data-apply-form]').forEach((form) => {
    const card = form.closest('[data-form-card]');
    const status = form.querySelector('[data-form-status]');
    const submit = form.querySelector('[data-submit]');
    const phone = form.querySelector('[data-phone]');
    const touched = new Set();

    phone.addEventListener('input', () => {
      const atEnd = phone.selectionStart === phone.value.length;
      const formatted = formatPhone(phone.value);
      if (atEnd || formatted.length >= phone.value.length) phone.value = formatted;
    });

    Object.keys(LABELS).forEach((name) => {
      const wrap = form.querySelector(`[data-field="${name}"]`);
      if (!wrap) return;
      const recheck = () => { if (touched.has(name) || wrap.classList.contains('is-invalid')) setError(form, name, validate(form, name)); };
      if (wrap.dataset.groupRequired) {
        wrap.addEventListener('change', () => { touched.add(name); setError(form, name, validate(form, name)); });
        return;
      }
      const el = form.elements[name];
      el.addEventListener('blur', () => {
        if (!el.value && el.type !== 'checkbox' && !touched.has(name)) return; // don't scold a field just tabbed past
        touched.add(name);
        setError(form, name, validate(form, name));
      });
      el.addEventListener(el.type === 'checkbox' || el.tagName === 'SELECT' ? 'change' : 'input', recheck);
    });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      status.textContent = '';
      const errors = {};
      Object.keys(LABELS).forEach((name) => {
        const msg = validate(form, name);
        touched.add(name);
        setError(form, name, msg);
        if (msg) errors[name] = msg;
      });
      if (Object.keys(errors).length) { showSummary(form, errors); return; }
      form.querySelector('[data-error-summary]').hidden = true;

      submit.classList.add('is-loading');
      submit.disabled = true;
      form.setAttribute('aria-busy', 'true');
      status.textContent = 'Sending your application...';
      const wait = MIN_FILL_MS - (Date.now() - loadedAt);
      if (wait > 0) await new Promise((r) => setTimeout(r, wait));

      const done = () => { submit.classList.remove('is-loading'); submit.disabled = false; form.removeAttribute('aria-busy'); status.textContent = ''; };
      try {
        const res = await fetch(form.action, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json', 'X-Requested-With': 'fetch' },
          body: new URLSearchParams(new FormData(form)),
          credentials: 'same-origin',
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          submit.classList.remove('is-loading');
          submit.classList.add('is-success');
          status.textContent = 'Application sent. Thank you.';
          setTimeout(() => showSuccess(card, data.firstName), 900);
          return;
        }
        done();
        if (data.errors) Object.entries(data.errors).forEach(([name, msg]) => setError(form, name, msg));
        showSummary(form, data.errors || {}, data.message || 'Something went wrong. Please try again, or email us directly.');
      } catch (err) {
        done();
        showSummary(form, {}, 'We could not reach our server. Please check your connection and try again, or email us directly.');
      }
    });
  });
}
